/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { connect, useDispatch } from 'react-redux'
import { debounce } from 'lodash'
import { ThemeContext } from 'styled-components'

// ui-kit
import { Row, Col } from '@navent-jobs/ui-kit/components/grid'
import { Icon } from '@navent-jobs/ui-kit/components/icon'
import Text from '@navent-jobs/ui-kit/components/text'

// components
import CheckSalario from './components/CheckSalario'
import LabelSalario from './components/LabelSalario'

// mixins
import {
  InputSalario,
  ButtonPostularme,
  HelperTextSalario,
  MessaggeError,
  LinkCustom,
  Spacer,
  Container,
} from './mixins'
import { SITE_ID } from '../../../../../../../constants'
import features from '../../../../../../../constants/features-per-country'
import Calculadora from './components/Calculadora'
import ModalCalculadora from './components/ModalCalculadora'
import { calculateSalarioNeto } from '../../../../../../../redux/actions/promedioSalarial'

const promedioSalarial = features[SITE_ID || 'BMAR'].promedioSalarial.enabled

const Component = ({
  isLoading,
  isMobile,
  isTabletPortrait,
  productoLookAndFeel,
  actualizarSalario,
  setActualizarSalario,
  salarioBrutoPretendido,
  setSalarioPretendido,
  salarioRequerido,
  handlePostularme,
  showComponentSalarioPretendido,
  aviso,
  calculadora,
  postulacionRapida,
}) => {
  const [isPinned, setIsPinned] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [focusInput, setFocusInput] = useState(false)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isMobileOrPortrait = isMobile || isTabletPortrait
  const preTabletLandscapeMinWidth = 992
  const preMobileMinWidth = 575
  const showErrorUnderInput = window.outerWidth >= preTabletLandscapeMinWidth || window.outerWidth <= preMobileMinWidth
  const { colors } = useContext<{ colors }>(ThemeContext)
  const calcularSalario = salarioBruto => {
    dispatch(calculateSalarioNeto(salarioBruto))
  }

  const getSalarioDebounce = useCallback(debounce(calcularSalario, 500), [])

  const { register, errors, handleSubmit, control } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  let maskOptions: any = t('currencyMask', { returnObjects: true })

  if (maskOptions === 'currencyMask') {
    maskOptions = {
      decimalSymbol: ',',
      prefix: '',
    }
  }

  const SalarioComponent = () =>
    promedioSalarial && aviso.area.stringId ? (
      <>
        {' '}
        <Text size="xs" variant="gray60" as="span">
          {t('fichaAviso.inputSalario.helperText.salarioPromedioPrimero')}
        </Text>
        <LinkCustom
          key="salario-pretendido-label"
          href={`/salarios/${aviso?.area?.stringId}`}
          linkoptions={{ variant: 'darken', type: 'link', target: '_blank' }}
        >
          {t('fichaAviso.inputSalario.helperText.salarioPromedioLink')}
        </LinkCustom>
        <Text size="xs" variant="gray60" as="span">
          {t('fichaAviso.inputSalario.helperText.salarioPromedioSegundo')}
        </Text>
      </>
    ) : (
      <></>
    )

  const ErrorComponent = () =>
    errors.salarioPretendido?.type !== 'required' ? (
      <HelperTextSalario>
        <Text size="xs" variant="gray60">
          {t('fichaAviso.inputSalario.helperText.default')}
          <SalarioComponent />
        </Text>
      </HelperTextSalario>
    ) : (
      <></>
    )

  const scrollToAnchor = () => {
    if (isMobile || isTabletPortrait) {
      const descriptionHeight = document.getElementById('ficha-detalle')?.getBoundingClientRect().height || 0
      if (descriptionHeight) {
        const lookAndFeelHeight = document.getElementById('look-and-feel')?.getBoundingClientRect().height || 0
        setTimeout(() => {
          window.scrollTo({ top: descriptionHeight + lookAndFeelHeight + 10, behavior: 'smooth' })
          setFocusInput(true)
        }, 200)
      }
    }
  }

  useEffect(() => {
    if (focusInput && isPinned && isMobileOrPortrait) {
      setFocusInput(false)
      const inputSalario = document.getElementById('salarioPretendido') as HTMLInputElement
      if (inputSalario) {
        setTimeout(() => {
          inputSalario.blur()
          inputSalario.focus()
        }, 500)
      }
    }
  }, [focusInput, isPinned, isMobileOrPortrait])

  useEffect(() => {
    const scrollFunction = () => {
      const prePostularBarTop = document.getElementById('pre-postular-bar')?.getBoundingClientRect().top || 0
      const headerHeight = document.getElementById('header-component')?.offsetHeight || 0
      const mobileTopActionBarHeight = document.getElementById('mobile-top-action-bar')?.offsetHeight || 0
      // altura de pantalla - header con tabs - header iconos - sticky container - margin (10) + posicion scroll
      const divPosition =
        window.screen.height - headerHeight - mobileTopActionBarHeight - prePostularBarTop - 10 + window.scrollY
      const scrolledToDiv = window.scrollY < divPosition

      setIsPinned(scrolledToDiv)
      const el = document.getElementById('sticky-postular-bar')
      if (el) {
        el.style.position = scrolledToDiv ? 'relative' : 'sticky'
      }
    }
    if (isMobileOrPortrait) {
      window.addEventListener('scroll', scrollFunction, true)
    }
    return () => {
      window.removeEventListener('scroll', scrollFunction, true)
    }
  }, [isMobileOrPortrait])

  useEffect(() => {
    getSalarioDebounce(salarioBrutoPretendido)
  }, [salarioBrutoPretendido])

  useEffect(() => {
    if (isMobileOrPortrait && errors.salarioPretendido?.type === 'required') {
      scrollToAnchor()
    }
  }, [errors.salarioPretendido?.type])

  return (
    <>
      <form id="form-salario-pretendido" onSubmit={handleSubmit(handlePostularme)}>
        <Row>
          <Col>
            <CheckSalario setActualizarSalario={setActualizarSalario} actualizarSalario={actualizarSalario} />
          </Col>
        </Row>
        <Row>
          {showComponentSalarioPretendido && (
            <>
              <Col
                sm={{ size: !isPinned && isMobileOrPortrait ? 7 : 12, order: 2 }}
                lg={{ size: 12, order: 1 }}
                onClick={() => scrollToAnchor()}
              >
                <InputSalario
                  id="salarioPretendido"
                  type="number"
                  name="salarioPretendido"
                  placeholder={
                    isMobile
                      ? t('fichaAviso.postularBar.sueldoPretendido')
                      : t('fichaAviso.postularBar.ingresaSueldoPretendido')
                  }
                  value={salarioBrutoPretendido}
                  onChange={event => {
                    if (isMobileOrPortrait) scrollToAnchor()
                    const salario = event.target.value
                      .slice(maskOptions.prefix.length)
                      .split(maskOptions.decimalSymbol)[0]
                      .replace(/\D+/g, '')
                    setSalarioPretendido(salario)
                  }}
                  onFocus={() => {}}
                  fieldOptions={{
                    variant: 'darken',
                    before: (
                      <Icon
                        name={(maskOptions && maskOptions.icon) || 'icon-light-money'}
                        color={colors.primary.lighten}
                        size="24"
                      />
                    ),
                    inputmode: 'numeric',
                  }}
                  maskOptions={maskOptions}
                  ref={register}
                  rules={{
                    required: {
                      value: salarioRequerido,
                      message: (
                        <MessaggeError>
                          <Text size="xs" variant="danger">
                            {t('fichaAviso.inputSalario.helperText.error')}
                          </Text>
                        </MessaggeError>
                      ),
                    },
                  }}
                  errors={errors}
                  control={control}
                  noBottomMargin
                  readonly={!isPinned && isMobileOrPortrait && 'readonly'}
                />

                {showErrorUnderInput && <ErrorComponent />}
              </Col>
            </>
          )}

          {(isPinned || !isMobileOrPortrait) && (
            <>
              <Col sm={{ size: 12, order: 3 }} lg={{ size: 12, order: 4 }}>
                {isMobileOrPortrait && <Spacer />}
                <LabelSalario />
              </Col>
              <Col sm={{ size: 12, order: 3 }}>
                <Spacer />
                <Calculadora setShowModal={setShowModal} calculadora={calculadora} />
              </Col>
            </>
          )}

          <Col
            sm={
              showComponentSalarioPretendido && (!isMobileOrPortrait || !isPinned)
                ? { size: 5, order: 2 }
                : { size: 12, order: 5 }
            }
            lg={{ size: 12, order: 5 }}
          >
            <ButtonPostularme
              buttonoptions={{ variant: 'primary', block: true }}
              productoLookAndFeel={productoLookAndFeel}
              form="form-salario-pretendido"
              type="submit"
              disabled={isLoading}
              isLoading={isLoading}
              isPinned={isPinned}
            >
              {postulacionRapida ? (
                <Container>
                  <Icon name="icon-light-flash-on" color={colors.gray0} />
                  {t('fichaAviso.postulacionRapida')}
                </Container>
              ) : (
                t('fichaAviso.postularme')
              )}
            </ButtonPostularme>
          </Col>
          {!showErrorUnderInput && (
            <Col sm={{ size: 12, order: 2 }}>
              <ErrorComponent />
            </Col>
          )}
        </Row>
      </form>
      <ModalCalculadora
        calculadora={calculadora}
        visible={showModal}
        isLoading={isLoading}
        closeModal={() => setShowModal(false)}
      />
    </>
  )
}

const states = ({ promedioSalarialStore }) => {
  return {
    calculadora: promedioSalarialStore.calculadora,
  }
}

export default connect(states)(Component)
